import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAppContext } from '../context/AppContext';
import { supabase } from '../lib/supabase';
import { CheckCircle, AlertTriangle, XCircle, Activity, Clock, Server } from 'lucide-react';
import { MaintenanceEvent } from '../components/admin/MaintenanceManager';
export function ServerStatus() {
  const {
    serverConnections
  } = useAppContext();
  const [events, setEvents] = useState<MaintenanceEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    async function loadEvents() {
      try {
        const {
          data,
          error
        } = await supabase.from('site_settings').select('value').eq('key', 'maintenance_events').maybeSingle();
        if (!error && data?.value) {
          setEvents(data.value as MaintenanceEvent[]);
        }
      } catch (err) {
        console.error('Failed to load maintenance events:', err);
      } finally {
        setIsLoading(false);
      }
    }
    loadEvents();
  }, []);
  // Calculate overall status
  const errorServers = serverConnections.filter(s => s.status === 'error');
  const disconnectedServers = serverConnections.filter(s => s.status === 'disconnected');
  let overallStatus: 'operational' | 'partial' | 'major' = 'operational';
  if (serverConnections.length > 0) {
    if (errorServers.length === serverConnections.length) {
      overallStatus = 'major';
    } else if (errorServers.length > 0 || disconnectedServers.length > 0) {
      overallStatus = 'partial';
    }
  }
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'operational':
        return {
          label: 'All Systems Operational',
          color: 'text-emerald-500',
          bg: 'bg-emerald-500',
          icon: CheckCircle
        };
      case 'partial':
        return {
          label: 'Partial System Outage',
          color: 'text-amber-500',
          bg: 'bg-amber-500',
          icon: AlertTriangle
        };
      case 'major':
        return {
          label: 'Major System Outage',
          color: 'text-red-500',
          bg: 'bg-red-500',
          icon: XCircle
        };
      default:
        return {
          label: 'Unknown Status',
          color: 'text-slate-500',
          bg: 'bg-slate-500',
          icon: Activity
        };
    }
  };
  const config = getStatusConfig(overallStatus);
  const StatusIcon = config.icon;
  // Generate 90 days of uptime data (simulated)
  const generateUptimeData = (serverStatus: string) => {
    const days = [];
    for (let i = 0; i < 90; i++) {
      // If server is currently in error, make the last few days red/yellow
      if (serverStatus === 'error' && i > 85) {
        days.push('bg-red-500');
      } else if (serverStatus === 'disconnected' && i > 88) {
        days.push('bg-amber-500');
      } else {
        // Randomly add a few yellow/red days for realism, but mostly green
        const rand = Math.random();
        if (rand > 0.98) days.push('bg-amber-500');else if (rand > 0.995) days.push('bg-red-500');else days.push('bg-emerald-500');
      }
    }
    return days;
  };
  const getRelativeTime = (dateString: string) => {
    const rtf = new Intl.RelativeTimeFormat('en', {
      numeric: 'auto'
    });
    const daysDifference = Math.round((new Date(dateString).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    if (Math.abs(daysDifference) < 1) {
      const hoursDiff = Math.round((new Date(dateString).getTime() - new Date().getTime()) / (1000 * 60 * 60));
      if (Math.abs(hoursDiff) < 1) {
        const minsDiff = Math.round((new Date(dateString).getTime() - new Date().getTime()) / (1000 * 60));
        return rtf.format(minsDiff, 'minute');
      }
      return rtf.format(hoursDiff, 'hour');
    }
    return rtf.format(daysDifference, 'day');
  };
  const activeEvents = events.filter(e => ['scheduled', 'in-progress'].includes(e.status));
  const pastEvents = events.filter(e => ['completed', 'resolved'].includes(e.status)).slice(0, 5);
  return <div className="min-h-screen bg-slate-950 flex flex-col" data-id="element-2575">
      <Navbar data-id="element-2576" />

      <main className="flex-1 pt-32 pb-24" data-id="element-2577">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl" data-id="element-2578">
          {/* Header Status */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-12" data-id="element-2579">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6" data-id="element-2580">
              System Status
            </h1>

            <div className={`p-6 rounded-2xl border flex items-center gap-4 ${overallStatus === 'operational' ? 'bg-emerald-500/10 border-emerald-500/20' : overallStatus === 'partial' ? 'bg-amber-500/10 border-amber-500/20' : 'bg-red-500/10 border-red-500/20'}`} data-id="element-2581">
              <div className="relative flex items-center justify-center" data-id="element-2582">
                <StatusIcon className={`w-10 h-10 ${config.color} relative z-10`} data-id="element-2583" />
                <div className={`absolute inset-0 ${config.bg} rounded-full blur-xl opacity-50 animate-pulse`} data-id="element-2584"></div>
              </div>
              <div data-id="element-2585">
                <h2 className={`text-2xl font-bold ${config.color}`} data-id="element-2586">
                  {config.label}
                </h2>
                <p className="text-slate-400 mt-1" data-id="element-2587">
                  Last updated: {new Date().toLocaleTimeString()}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Server Grid */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="mb-16" data-id="element-2588">
            <h3 className="text-xl font-bold text-white mb-6" data-id="element-2589">
              Connected Services
            </h3>

            {serverConnections.length === 0 ? <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center text-slate-400" data-id="element-2590">
                No public services configured.
              </div> : <div className="grid grid-cols-1 gap-4" data-id="element-2591">
                {serverConnections.map((server, i) => {
              const uptimeData = generateUptimeData(server.status);
              const isUp = server.status === 'connected';
              return <div key={server.id} className="bg-slate-900 border border-slate-800 rounded-2xl p-6" data-id="element-2592">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-2593">
                        <div className="flex items-center gap-4" data-id="element-2594">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isUp ? 'bg-emerald-500/10' : 'bg-red-500/10'}`} data-id="element-2595">
                            <Server className={isUp ? 'text-emerald-500' : 'text-red-500'} data-id="element-2596" />
                          </div>
                          <div data-id="element-2597">
                            <h4 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-2598">
                              {server.name}
                              <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-800 text-slate-400 capitalize" data-id="element-2599">
                                {server.type}
                              </span>
                            </h4>
                            <p className="text-sm text-slate-400" data-id="element-2600">
                              Last checked {getRelativeTime(server.lastSync)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right" data-id="element-2601">
                          <div className={`text-lg font-bold ${isUp ? 'text-emerald-500' : 'text-red-500'}`} data-id="element-2602">
                            {isUp ? '99.9%' : '0.0%'}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-2603">
                            Uptime (90d)
                          </div>
                        </div>
                      </div>

                      {/* Uptime Bar */}
                      <div className="space-y-2" data-id="element-2604">
                        <div className="flex gap-0.5 h-8" data-id="element-2605">
                          {uptimeData.map((color, idx) => <div key={idx} className={`flex-1 rounded-sm ${color} opacity-80 hover:opacity-100 transition-opacity cursor-help`} title={`Day ${90 - idx} ago`} data-id="element-2606" />)}
                        </div>
                        <div className="flex justify-between text-xs text-slate-500 font-medium" data-id="element-2607">
                          <span data-id="element-2608">90 days ago</span>
                          <span data-id="element-2609">100% Uptime</span>
                          <span data-id="element-2610">Today</span>
                        </div>
                      </div>
                    </div>;
            })}
              </div>}
          </motion.div>

          {/* Active Incidents / Maintenance */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} data-id="element-2611">
            <h3 className="text-xl font-bold text-white mb-6" data-id="element-2612">
              Active Incidents & Maintenance
            </h3>

            {isLoading ? <div className="flex justify-center py-8" data-id="element-2613">
                <div className="w-8 h-8 border-2 border-fox-500/30 border-t-fox-500 rounded-full animate-spin" data-id="element-2614" />
              </div> : activeEvents.length === 0 ? <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center flex flex-col items-center" data-id="element-2615">
                <CheckCircle className="w-12 h-12 text-emerald-500 mb-4 opacity-50" data-id="element-2616" />
                <p className="text-slate-400" data-id="element-2617">
                  No active incidents or scheduled maintenance.
                </p>
              </div> : <div className="space-y-6" data-id="element-2618">
                {activeEvents.map(event => <div key={event.id} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 relative overflow-hidden" data-id="element-2619">
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${event.severity === 'critical' ? 'bg-red-500' : event.severity === 'major' ? 'bg-orange-500' : event.severity === 'minor' ? 'bg-amber-500' : 'bg-blue-500'}`} data-id="element-2620" />

                    <div className="pl-4" data-id="element-2621">
                      <div className="flex flex-wrap items-center gap-3 mb-3" data-id="element-2622">
                        <h4 className="text-lg font-bold text-white" data-id="element-2623">
                          {event.title}
                        </h4>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${event.status === 'in-progress' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : 'bg-blue-500/10 text-blue-500 border-blue-500/20'}`} data-id="element-2624">
                          {event.status.replace('-', ' ')}
                        </span>
                      </div>

                      <p className="text-slate-300 mb-6" data-id="element-2625">{event.description}</p>

                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-6" data-id="element-2626">
                        <div className="flex items-center gap-1.5" data-id="element-2627">
                          <Clock size={16} data-id="element-2628" />
                          <span data-id="element-2629">
                            Started:{' '}
                            {new Date(event.startTime).toLocaleString()}
                          </span>
                        </div>
                        {event.endTime && <div className="flex items-center gap-1.5" data-id="element-2630">
                            <Activity size={16} data-id="element-2631" />
                            <span data-id="element-2632">
                              Est. End:{' '}
                              {new Date(event.endTime).toLocaleString()}
                            </span>
                          </div>}
                      </div>

                      {event.updates && event.updates.length > 0 && <div className="space-y-4 border-t border-slate-800 pt-6" data-id="element-2633">
                          {event.updates.map((update, i) => <div key={i} className="relative pl-6" data-id="element-2634">
                              <div className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-slate-600" data-id="element-2635" />
                              {i !== event.updates.length - 1 && <div className="absolute left-[3px] top-4 bottom-[-20px] w-px bg-slate-800" data-id="element-2636" />}
                              <div className="text-xs font-medium text-slate-500 mb-1" data-id="element-2637">
                                {new Date(update.time).toLocaleString()}
                              </div>
                              <div className="text-sm text-slate-300" data-id="element-2638">
                                {update.message}
                              </div>
                            </div>)}
                        </div>}
                    </div>
                  </div>)}
              </div>}
          </motion.div>

          {/* Past Incidents */}
          {pastEvents.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="mt-16" data-id="element-2639">
              <h3 className="text-xl font-bold text-white mb-6" data-id="element-2640">
                Past Incidents
              </h3>
              <div className="space-y-4" data-id="element-2641">
                {pastEvents.map(event => <div key={event.id} className="bg-slate-900/50 border border-slate-800 rounded-xl p-5" data-id="element-2642">
                    <div className="flex items-center justify-between mb-2" data-id="element-2643">
                      <h4 className="font-bold text-white" data-id="element-2644">{event.title}</h4>
                      <span className="text-xs text-slate-500" data-id="element-2645">
                        {new Date(event.startTime).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 mb-3" data-id="element-2646">
                      {event.description}
                    </p>
                    <span className="text-xs font-medium text-emerald-500 bg-emerald-500/10 px-2 py-1 rounded" data-id="element-2647">
                      Resolved
                    </span>
                  </div>)}
              </div>
            </motion.div>}
        </div>
      </main>
      <Footer data-id="element-2648" />
    </div>;
}